import type { Platform } from "../platform/Platform";

/**
 * Shared, framework-agnostic types for the Global Leaderboard feature.
 * Used by both the browser client (`src/online`) and the Netlify
 * Functions server layer (`netlify/functions`) so the two never drift on
 * field names/shapes - imported by relative path from the functions side
 * since they live outside `src`'s `@/` alias.
 */

export type RunStatus = "completed" | "failed";
export type DeathReason = "wall" | "obstacle" | "self";
export type RunSource = "play" | "replay" | "next_level" | "level_select";
export type Stars = 0 | 1 | 2 | 3;
/** Why a completed run's gate was open. `length_target`: the real logical snake length reached the level's `targetLength`. `starfruit_gate`: the level was completed through the rare STARFRUIT's temporary gate, so `finalLength` may legitimately be below `targetLength`. */
export type CompletionReason = "length_target" | "starfruit_gate";

/**
 * Scoring V2: every run this client build submits is `scoringVersion: 2` -
 * `FINAL SCORE = BITE SCORE + SKILL SCORE + PACE BONUS`, `score` itself
 * carries the Final Score. Existing rows/old-client submissions remain
 * `scoringVersion: 1` (where `score` means Bite Score, exactly as
 * before) - the two are never compared or ranked against each other, see
 * `leaderboardOrdering.ts` and `supabase/migrations/003_scoring_v2.sql`.
 */
export const CURRENT_SCORING_VERSION = 2;

/** Everything one run submits, except `playerId` (always derived from the caller's JWT, never sent in the body). */
export interface RunSubmission {
  runId: string;
  levelId: string;
  worldId: string;
  status: RunStatus;
  score: number;
  stars: Stars;
  finalLength: number;
  /** Only time the player had active control - see RunTracker. */
  activeDurationMs: number;
  safeCount: number;
  rushCount: number;
  chaosCount: number;
  maxRushStreak: number;
  jackpotCount: number;
  speedTrialStartedCount: number;
  speedTrialSurvivedCount: number;
  scoreHitCount: number;
  shrinkCount: number;
  /** Number of times the WORMHOLE CHAOS outcome activated this run. */
  wormholeCount: number;
  /** 0 or 1 - STARFRUIT can appear at most once per run. */
  starfruitSpawnedCount: number;
  /** 0 or 1 - STARFRUIT can be eaten at most once per run. */
  starfruitEatenCount: number;
  /** `null` for a failed run; required and validated against `finalLength`/`starfruitEatenCount` for a completed one. */
  completionReason: CompletionReason | null;
  deathReason: DeathReason | null;
  runSource: RunSource;

  // -- Scoring V2 (see CURRENT_SCORING_VERSION doc above) --
  scoringVersion: number;
  /** The existing fruit-scoring system, unchanged - equals `score` for a `scoringVersion: 1` row. */
  biteScore: number;
  skillScore: number;
  /** Always 0 for a failed run or a `starfruit_gate` completion. */
  paceBonus: number;
  moveCount: number;
  perfectFoldCount: number;
  lastSecondTurnCount: number;
  tailGrazeCount: number;
  threadTheNeedleCount: number;
  doubleDangerCount: number;
  bestFlowStreak: number;
  pauseCount: number;
  pausedDurationMs: number;
  /** TRICK BITE (a fruit eaten within 3 moves of a credited maneuver) count - flat +20 each, already included in `skillScore`. See `TrickBiteTracker`. */
  trickBiteCount: number;
  /** CLUTCH BITE (a fruit eaten on the exact same move a maneuver credits) count - flat +35 each, already included in `skillScore`. Mutually exclusive with TRICK BITE per fruit - never both for the same one. */
  clutchBiteCount: number;
  /**
   * PR34: the Mobile/Desktop category this run was played in, frozen at run
   * start (`detectPlatform()`). Decides which leaderboard the result lands on
   * and, for Mobile Mars/Saturn/Sun, which gate target the server validates
   * against. Always set by this client build; optional only because a run
   * queued by a pre-PR34 build never had one (stored as `unknown`).
   */
  platform?: Platform;
}

/** Minimal catalog entry needed to validate a submitted run server-side. */
export interface LevelCatalogEntry {
  levelId: string;
  worldId: string;
  targetLength: number;
  active: boolean;
}

/** `score` is Final Score V2 (`SupabaseLeaderboardService` reads `get_level_leaderboard_v2` for this - see that file). Never a mix of V1/V2 rows - the RPC itself filters `scoring_version = 2` server-side. */
export interface LevelLeaderboardEntry {
  rank: number;
  displayName: string;
  score: number;
  stars: Stars;
}

/** `totalScore`/tie-break are Final Score V2 only, summed across a world's levels - only for a player with a V2 result on every level in that world (`get_world_leaderboard_v2`). `totalStars` is still summed and shown (still Bite-Score-derived), but is never part of the ranking itself. */
export interface WorldLeaderboardEntry {
  rank: number;
  displayName: string;
  totalScore: number;
  totalStars: number;
}

/** V2-only (`get_my_level_rank_v2`) - never resolved by comparing against a V1 score. */
export interface MyLevelRank {
  hasScore: boolean;
  rank: number | null;
  score: number;
  stars: Stars;
}

/** V2-only (`get_my_world_rank_v2`) - `hasScore` is only true once every level in the world has a V2 result for this player specifically; a mix of V1-only and V2-only level results never satisfies this. */
export interface MyWorldRank {
  hasScore: boolean;
  rank: number | null;
  totalScore: number;
  totalStars: number;
}

/** Outcome of a read from any LeaderboardService implementation - callers branch on `state`, never on thrown errors, so a server/network failure can never crash a UI screen. */
export type ServiceResult<T> =
  | { state: "ok"; data: T }
  | { state: "loading" }
  | { state: "offline" }
  | { state: "error"; message: string };
